<script setup>
import { inject, onMounted, computed, ref } from "vue";
import * as d3 from "d3";
import { useTrackStore } from "@/stores/TrackStore.js";
import { useCartStore } from "@/stores/CartStore";
import { useFeelingStore } from "@/stores/FeelingStore.js";
import { storeToRefs } from "pinia";

const audioPlayer = inject("audioPlayer");
const trackStore = useTrackStore();
const cartStore = useCartStore();
const { playingTrack } = storeToRefs(trackStore);
const feelingStore = useFeelingStore();
const feelingsData = feelingStore.feelingsData;
const delta = Math.PI; // where the wheel opens in rad (0 is top, PI/2 is right...)
const width = 932;
const animationDuration = 1500;
const viewBox = `0,0,${width},${width}`;
const transform = `translate(${width / 2},${width / 2})`;
const radius = width / 5;
const canGoBack = computed(() => feelingStore.selectedFeeling !== null);
const canPlay = audioPlayer.canPlay;
const playing = audioPlayer.playing;
const targetProgress = ref(0);
const progressRatio = audioPlayer.progressRatio;
const progressTime = audioPlayer.progressTime;

const stopPlay = function stopPlay() {
  audioPlayer.stop();
};

const togglePlay = function togglePlay() {
  audioPlayer.toggle();
};

const buyTrack = function buyTrack() {
  cartStore.add(playingTrack.value);
};

const seekTo = function seekTo() {
  audioPlayer.seekTo(targetProgress.value);
};

const resetTargetProgress = function resetTargetProgress() {
  targetProgress.value = 0;
};

const calculateTargetProgress = function calculateTargetProgress(event) {
  const rect = event.target.getBoundingClientRect();
  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;
  const deltaX = event.clientX - centerX;
  const deltaY = event.clientY - centerY;
  const radians = Math.atan2(-deltaX, deltaY);
  return 0.5 + radians / 2 / Math.PI;
};

const updateTargetProgress = function updateTargetProgress(event) {
  if (canPlay) {
    targetProgress.value = calculateTargetProgress(event);
  }
};

const selectFeeling = function selectFeeling(feeling) {
  feelingStore.selectedFeeling = feeling;
  trackStore.selectFeeling(feeling);
};

const selectTrack = function selectTrack(track) {
  trackStore.selectTrack(track);
};

const progressArc = computed(() =>
  d3
    .arc()
    .innerRadius(160)
    .outerRadius(185.5)
    .startAngle(0)
    .endAngle(progressRatio.value * 2 * Math.PI)()
);

const remainingArc = computed(() =>
  d3
    .arc()
    .innerRadius(160)
    .outerRadius(185.5)
    .startAngle(progressRatio.value * 2 * Math.PI)
    .endAngle(2 * Math.PI)()
);

const indicatorArc = computed(() =>
  d3
    .arc()
    .innerRadius(160)
    .outerRadius(185.5)
    .startAngle(0)
    .endAngle(targetProgress.value * 2 * Math.PI)()
);

const seekingArc = d3
  .arc()
  .innerRadius(160)
  .outerRadius(185.5)
  .startAngle(0)
  .endAngle(2 * Math.PI)();

const generateSvgNode = function generateSvgNode() {
  const _partition = function _partition(data) {
    const root = d3.hierarchy(data).sum((d) => d.value);
    return d3.partition().size([2 * Math.PI, root.height + 1])(root);
  };
  const _arcVisible = (d) => d.y1 <= 2 && d.y0 >= 0 && d.x1 > d.x0;
  const _arcClickable = (d) => d.y1 <= 2 && d.y0 >= 1 && d.x1 > d.x0;
  const _fillOpacity = (arc, children) =>
    _arcVisible(arc) ? (children ? 0.7 : 0.3) : 0;
  const _labelVisible = (d) =>
    d.y1 <= 2 && d.y0 >= 0 && (d.y1 - d.y0) * (d.x1 - d.x0) > 0.03;
  const _labelClickable = (d) =>
    d.x1 - d.x0 > 0.001 && (d.y1 == 2 || d.y1 == 0);
  const _labelTransform = function (d) {
    // function (d, _datum)
    const x = (((d.x0 + d.x1 + 2 * delta) / 2) * 180) / Math.PI;
    let y = ((d.y0 + d.y1) / 2) * radius;
    y = y - (1 - d.y0) * 30; // Translate label to center for center circle
    return `rotate(${x - 90}) translate(${y},0) rotate(${
      x >= 270 && x < 450 ? 90 : 270
    })`;
  };

  const _arcClicked = function _arcClicked(event, p) {
    if (_arcClickable(p.current)) {
      _clicked(event, p);
    }
  };
  const _labelClicked = function _labelClicked(event, p) {
    if (_labelClickable(p.current)) {
      _clicked(event, p);
    }
  };
  const _clicked = function _clicked(event, p) {
    if (!p) {
      return;
    }

    wheelBackground
      .datum(p == root ? null : p.parent)
      .style("cursor", p == root ? "default" : "pointer")
      .attr("pointer-events", p == root ? "none" : "all");

    selectFeeling(p == root ? null : p.data);

    root.each(
      (d) =>
        (d.target = {
          x0:
            Math.max(0, Math.min(1, (d.x0 - p.x0) / (p.x1 - p.x0))) *
            2 *
            Math.PI,
          x1:
            Math.max(0, Math.min(1, (d.x1 - p.x0) / (p.x1 - p.x0))) *
            2 *
            Math.PI,
          y0: Math.max(0, d.y0 - p.depth),
          y1: Math.max(0, d.y1 - p.depth),
        })
    );

    const t = g.transition().duration(animationDuration);

    path
      .transition(t)
      .tween("data", (d) => {
        const i = d3.interpolate(d.current, d.target);
        return (t) => (d.current = i(t));
      })
      .filter(function (d) {
        return +this.getAttribute("fill-opacity") || _arcVisible(d.target);
      })
      .attr("fill-opacity", (d) => _fillOpacity(d.target, d.children))
      .style("cursor", (d) => (_arcClickable(d.target) ? "pointer" : "default"))
      .style("pointer-events", (d) =>
        _arcClickable(d.target) ? "all" : "none"
      )
      .attrTween("d", (d) => () => feelingArc(d.current));

    label
      .filter(function (d) {
        return +this.getAttribute("fill-opacity") || _labelVisible(d.target);
      })
      .transition(t)
      .style("cursor", (d) =>
        _labelClickable(d.target) ? "pointer" : "default"
      )
      .attr("pointer-events", (d) =>
        _labelClickable(d.target) ? "all" : "none"
      )
      .attr("fill-opacity", (d) => +_labelVisible(d.target))
      .attrTween("transform", (d) => () => _labelTransform(d.current, d));
  };

  const root = _partition(feelingsData);
  root.each((d) => (d.current = d));

  const g = d3.create("svg").append("g");

  const wheelBackground = d3
    .select(document.getElementById("wheel_background"))
    .on("click", _clicked);

  const feelingArc = d3
    .arc()
    .startAngle((d) => d.x0 + delta)
    .endAngle((d) => d.x1 + delta)
    .padAngle((d) => 0.1 * d.y0)
    .padRadius(radius * 1.5)
    .innerRadius((d) => d.y0 * radius)
    .outerRadius((d) => Math.max(d.y0 * radius, d.y1 * radius - 1));

  const path = g
    .selectAll("path")
    .data(root)
    .join("path")
    .attr("class", (d) => "wheel-feeling-arc " + d.data.name.toLowerCase())
    .attr("fill", (d) => {
      if (d.depth == 0) return "transparent";
      while (d.depth > 1) d = d.parent;
      return d.data.color;
    })
    .attr("fill-opacity", (d) => _fillOpacity(d.current, d.children))
    .style("cursor", (d) => (_arcClickable(d.current) ? "pointer" : "default"))
    .attr("pointer-events", (d) => (_arcClickable(d.current) ? "all" : "none"))
    .attr("d", (d) => feelingArc(d.current))
    .on("click", _arcClicked);

  const label = g
    .attr("text-anchor", "middle")
    .style("user-select", "none")
    .selectAll("text")
    .data(root)
    .join("text")
    .attr("dy", "0.35em")
    .style("cursor", (d) =>
      _labelClickable(d.current) ? "pointer" : "default"
    )
    .attr("pointer-events", (d) =>
      _labelClickable(d.current) ? "all" : "none"
    )
    .attr("fill-opacity", (d) => +_labelVisible(d.current))
    .attr("transform", (d) => _labelTransform(d.current, d))
    .text((d) => d.data.name.toUpperCase())
    .on("click", _labelClicked);

  return g.node();
};

onMounted(() => {
  document.getElementById("wheel_injection_target").prepend(generateSvgNode());
});
</script>

<template>
  <div id="feelings_wheel" class="text-center user-select-none col">
    <svg :viewBox="viewBox" style="font: 20px 'Work Sans', sans-serif">
      <defs>
        <radialGradient id="radial-gradient">
          <stop offset="0%" stop-color="#fff"></stop>
          <stop offset="100%" stop-color="#ffffff00"></stop>
        </radialGradient>
      </defs>
      <rect
        id="wheel_background"
        width="100%"
        height="100%"
        fill="url(#radial-gradient)"
      ></rect>
      <g id="wheel_injection_target" :transform="transform">
        <text
          text-anchor="middle"
          id="feelings_wheel_artist_name"
          @click="selectTrack(playingTrack)"
          dy="-0.5em"
          style="font-size: 100%; cursor: pointer"
          opacity="0.8"
          fill="#000"
        >
          {{ playingTrack ? playingTrack.artistName : "" }}
        </text>
        <text
          text-anchor="middle"
          id="feelings_wheel_track_name"
          @click="selectTrack(playingTrack)"
          dy="0.5em"
          style="font-size: 100%; cursor: pointer"
          opacity="0.8"
          fill="#000"
        >
          {{ playingTrack ? playingTrack.title : "" }}
        </text>
        <text
          v-show="canGoBack"
          text-anchor="middle"
          id="feelings_wheel_back"
          dy="-2.8em"
          style="font-size: 200%; cursor: pointer"
          opacity="0.6"
          fill="#000"
          class="fas"
          pointer-events="none"
        >
          &#xF052;
        </text>
        <text
          v-show="canPlay"
          @click="stopPlay"
          text-anchor="middle"
          id="feelings_wheel_stop"
          dx="-1.8em"
          dy="1.8em"
          style="font-size: 200%; cursor: pointer"
          opacity="0.6"
          fill="#000"
          class="fas"
          pointer-events="all"
        >
          &#xF04D;
        </text>
        <text
          v-show="canPlay"
          @click="togglePlay"
          text-anchor="middle"
          id="feelings_wheel_play"
          dx="0em"
          dy="1.8em"
          style="font-size: 200%; cursor: pointer"
          opacity="0.6"
          fill="#000"
          class="fas"
          pointer-events="all"
        >
          {{ playing ? "\uf04c" : "\uf04b" }}
        </text>
        <text
          v-show="playingTrack"
          @click="buyTrack"
          text-anchor="middle"
          id="feelings_wheel_buy"
          dx="1.8em"
          dy="1.8em"
          style="font-size: 200%; cursor: pointer"
          opacity="0.6"
          fill="#000"
          class="fas"
          pointer-events="all"
        >
          &#xF218;
        </text>
        <text
          v-show="canPlay"
          text-anchor="middle"
          dy="6em"
          style="font-size: 100%; font-family: 'Noto Sans Mono'"
          opacity="0.6"
          pointer-events="none"
        >
          {{ progressTime }}
        </text>
        <path :d="progressArc" opacity="0.2" fill="#000"></path>
        <path :d="remainingArc" opacity="0.8" fill="#fff"></path>
        <path
          :d="indicatorArc"
          opacity="0.5"
          fill="#666"
          style="mix-blend-mode: color-burn"
        ></path>
        <path
          :d="seekingArc"
          @click="seekTo"
          @mouseout="resetTargetProgress"
          @mousemove="updateTargetProgress"
          opacity="0"
          fill="#fff"
          style="cursor: pointer"
          pointer-events="all"
        ></path>
      </g>
    </svg>
  </div>
</template>

<style>
body {
  background-color: #b3b3b3 !important;
}

#feelings_wheel svg,
#top_jumbotron svg {
  max-height: 80vh;
  min-height: 500px;
}

#top_jumbotron svg {
  margin-top: 66.625px;
}
</style>
